import React, { useState, useEffect } from 'react';
import { 
  Calendar, Plus, Edit2, Trash2, ChevronDown, ChevronRight, 
  Target, Rocket, Flag, Clock, CheckCircle, X, Play
} from 'lucide-react';
import { api } from '../api/client';

const ReleasesPage = () => {
  const [releases, setReleases] = useState([]);
  const [sprints, setSprints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedReleases, setExpandedReleases] = useState(new Set());
  const [showReleaseModal, setShowReleaseModal] = useState(false);
  const [showSprintModal, setShowSprintModal] = useState(false);
  const [editingRelease, setEditingRelease] = useState(null);
  const [editingSprint, setEditingSprint] = useState(null);
  const [selectedReleaseId, setSelectedReleaseId] = useState(null);
  
  const [releaseForm, setReleaseForm] = useState({
    name: '', start_date: '', end_date: '', description: ''
  });
  
  const [sprintForm, setSprintForm] = useState({
    name: '', start_date: '', end_date: '', goal: '', status: 'planned', velocity: 0, capacity: 0
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [releasesRes, sprintsRes] = await Promise.all([
        api.getReleases(),
        api.getSprints()
      ]);
      setReleases(releasesRes.data.releases || []);
      setSprints(sprintsRes.data.sprints || []);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleRelease = (releaseId) => {
    const newExpanded = new Set(expandedReleases);
    if (newExpanded.has(releaseId)) {
      newExpanded.delete(releaseId);
    } else {
      newExpanded.add(releaseId);
    }
    setExpandedReleases(newExpanded);
  };

  const getSprintsForRelease = (releaseId) => {
    return sprints.filter(s => s.release_id === releaseId);
  };

  const getUnassignedSprints = () => {
    return sprints.filter(s => !s.release_id);
  };

  // Release CRUD
  const openReleaseModal = (release = null) => {
    if (release) {
      setEditingRelease(release);
      setReleaseForm({
        name: release.name || '',
        start_date: release.start_date || '',
        end_date: release.end_date || '',
        description: release.description || ''
      });
    } else {
      setEditingRelease(null);
      setReleaseForm({ name: '', start_date: '', end_date: '', description: '' });
    }
    setShowReleaseModal(true);
  };

  const saveRelease = async () => {
    if (!releaseForm.name.trim()) {
      alert('Release name is required');
      return;
    }
    try {
      if (editingRelease) {
        await api.updateRelease(editingRelease.id, releaseForm);
      } else {
        await api.createRelease(releaseForm);
      }
      setShowReleaseModal(false);
      loadData();
    } catch (error) {
      alert('Failed to save release: ' + (error.response?.data?.detail || error.message));
    }
  };

  const deleteRelease = async (releaseId) => {
    if (!window.confirm('Delete this release? Sprints will become unassigned.')) return;
    try {
      await api.deleteRelease(releaseId);
      loadData();
    } catch (error) {
      alert('Failed to delete: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Sprint CRUD
  const openSprintModal = (sprint = null, releaseId = null) => {
    if (sprint) {
      setEditingSprint(sprint);
      setSprintForm({
        name: sprint.name || '',
        start_date: sprint.start_date || '',
        end_date: sprint.end_date || '',
        goal: sprint.goal || '',
        status: sprint.status || 'planned',
        velocity: sprint.velocity || 0,
        capacity: sprint.capacity || 0
      });
      setSelectedReleaseId(sprint.release_id || null);
    } else {
      setEditingSprint(null);
      setSprintForm({ name: '', start_date: '', end_date: '', goal: '', status: 'planned', velocity: 0, capacity: 0 });
      setSelectedReleaseId(releaseId);
    }
    setShowSprintModal(true);
  };

  const saveSprint = async () => {
    if (!sprintForm.name.trim()) {
      alert('Sprint name is required');
      return;
    }
    try {
      const data = { ...sprintForm, release_id: selectedReleaseId };
      if (editingSprint) {
        await api.updateSprint(editingSprint.id, data);
      } else {
        await api.createSprint(data);
      }
      setShowSprintModal(false);
      loadData();
    } catch (error) {
      alert('Failed to save sprint: ' + (error.response?.data?.detail || error.message));
    }
  };

  const deleteSprint = async (sprintId) => {
    if (!window.confirm('Delete this sprint?')) return;
    try {
      await api.deleteSprint(sprintId);
      loadData();
    } catch (error) {
      alert('Failed to delete: ' + (error.response?.data?.detail || error.message));
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      'planned': 'bg-[var(--clr-surface-subtle)] text-gray-700',
      'active': 'bg-blue-100 text-blue-700',
      'completed': 'bg-green-100 text-green-700'
    };
    return styles[status] || 'bg-[var(--clr-surface-subtle)] text-gray-700';
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'active': return <Play className="w-3 h-3" />;
      case 'completed': return <CheckCircle className="w-3 h-3" />;
      default: return <Clock className="w-3 h-3" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--clr-brand-primary)]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[var(--clr-text-primary)] flex items-center gap-3">
            <Rocket className="w-7 h-7 text-purple-600" />
            Releases & Sprints
          </h1>
          <p className="text-[var(--clr-text-secondary)] mt-1">Manage release cycles and sprint planning</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => openSprintModal(null, null)}
            className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
            data-testid="add-sprint-btn"
          >
            <Plus className="w-4 h-4" /> Add Sprint
          </button>
          <button
            onClick={() => openReleaseModal()}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            data-testid="add-release-btn"
          >
            <Plus className="w-4 h-4" /> Add Release
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-[var(--clr-surface-card)] rounded-lg border p-4">
          <p className="text-sm text-[var(--clr-text-secondary)] mb-1">Total Releases</p>
          <p className="text-2xl font-bold text-purple-600">{releases.length}</p>
        </div>
        <div className="bg-[var(--clr-surface-card)] rounded-lg border p-4">
          <p className="text-sm text-[var(--clr-text-secondary)] mb-1">Total Sprints</p>
          <p className="text-2xl font-bold text-teal-600">{sprints.length}</p>
        </div>
        <div className="bg-[var(--clr-surface-card)] rounded-lg border p-4">
          <p className="text-sm text-[var(--clr-text-secondary)] mb-1">Active Sprints</p>
          <p className="text-2xl font-bold text-[var(--clr-brand-primary)]">
            {sprints.filter(s => s.status === 'active').length}
          </p>
        </div>
        <div className="bg-[var(--clr-surface-card)] rounded-lg border p-4">
          <p className="text-sm text-[var(--clr-text-secondary)] mb-1">Completed Sprints</p>
          <p className="text-2xl font-bold text-[var(--clr-sev-healthy)]">
            {sprints.filter(s => s.status === 'completed').length}
          </p>
        </div>
      </div>

      {/* Releases List */}
      <div className="space-y-4">
        {releases.map(release => (
          <div key={release.id} className="bg-[var(--clr-surface-card)] rounded-xl border shadow-[var(--shadow-card)]" data-testid={`release-${release.id}`}>
            <div 
              className="p-4 flex items-center justify-between cursor-pointer hover:bg-[var(--clr-surface-hover)]"
              onClick={() => toggleRelease(release.id)}
            >
              <div className="flex items-center gap-3">
                <button className="p-1 hover:bg-[var(--clr-surface-hover)] rounded">
                  {expandedReleases.has(release.id) ? 
                    <ChevronDown className="w-5 h-5 text-[var(--clr-text-muted)]" /> : 
                    <ChevronRight className="w-5 h-5 text-[var(--clr-text-muted)]" />}
                </button>
                <Rocket className="w-5 h-5 text-purple-600" />
                <div>
                  <h3 className="font-semibold text-[var(--clr-text-primary)]">{release.name}</h3>
                  <div className="flex items-center gap-3 text-sm text-[var(--clr-text-muted)]">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {release.start_date || 'No start'} - {release.end_date || 'No end'}
                    </span>
                    <span className="flex items-center gap-1">
                      <Target className="w-3 h-3" />
                      {getSprintsForRelease(release.id).length} sprints
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                <button
                  onClick={() => openSprintModal(null, release.id)}
                  className="p-1.5 text-teal-600 hover:bg-teal-50 rounded"
                  title="Add Sprint"
                >
                  <Plus className="w-4 h-4" />
                </button>
                <button
                  onClick={() => openReleaseModal(release)}
                  className="p-1.5 text-[var(--clr-brand-primary)] hover:bg-[var(--clr-brand-primary)] bg-opacity-10 rounded"
                  title="Edit"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => deleteRelease(release.id)}
                  className="p-1.5 text-[var(--clr-sev-blocked)] hover:bg-[var(--clr-sev-blocked)] bg-opacity-10 rounded"
                  title="Delete"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            {/* Sprints under this release */}
            {expandedReleases.has(release.id) && (
              <div className="border-t bg-[var(--clr-surface-subtle)] p-4">
                {release.description && (
                  <p className="text-sm text-[var(--clr-text-secondary)] mb-4">{release.description}</p>
                )}
                <div className="space-y-2">
                  {getSprintsForRelease(release.id).length > 0 ? (
                    getSprintsForRelease(release.id).map(sprint => (
                      <div key={sprint.id} className="bg-[var(--clr-surface-card)] rounded-lg border p-3 flex items-center justify-between" data-testid={`sprint-${sprint.id}`}>
                        <div className="flex items-center gap-3">
                          <Flag className="w-4 h-4 text-teal-600" />
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{sprint.name}</span>
                              <span className={`px-2 py-0.5 rounded text-xs font-medium flex items-center gap-1 ${getStatusBadge(sprint.status)}`}>
                                {getStatusIcon(sprint.status)}
                                {sprint.status}
                              </span>
                            </div>
                            <div className="text-sm text-[var(--clr-text-muted)] flex gap-3">
                              <span>{sprint.start_date} - {sprint.end_date}</span>
                              {sprint.velocity > 0 && <span>Velocity: {sprint.velocity}</span>}
                            </div>
                            {sprint.goal && <p className="text-xs text-[var(--clr-text-muted)] mt-1">{sprint.goal}</p>}
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <button
                            onClick={() => openSprintModal(sprint)}
                            className="p-1 text-[var(--clr-brand-primary)] hover:bg-[var(--clr-brand-primary)] bg-opacity-10 rounded"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => deleteSprint(sprint.id)}
                            className="p-1 text-[var(--clr-sev-blocked)] hover:bg-[var(--clr-sev-blocked)] bg-opacity-10 rounded"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-[var(--clr-text-muted)] text-center py-4">No sprints in this release</p>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}

        {/* Unassigned Sprints */}
        {getUnassignedSprints().length > 0 && (
          <div className="bg-[var(--clr-surface-card)] rounded-xl border shadow-[var(--shadow-card)]">
            <div className="p-4 border-b">
              <h3 className="font-semibold text-[var(--clr-text-primary)] flex items-center gap-2">
                <Flag className="w-5 h-5 text-[var(--clr-text-muted)]" />
                Unassigned Sprints ({getUnassignedSprints().length})
              </h3>
            </div>
            <div className="p-4 space-y-2">
              {getUnassignedSprints().map(sprint => (
                <div key={sprint.id} className="bg-[var(--clr-surface-subtle)] rounded-lg border p-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Flag className="w-4 h-4 text-[var(--clr-text-muted)]" />
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{sprint.name}</span>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium flex items-center gap-1 ${getStatusBadge(sprint.status)}`}>
                          {getStatusIcon(sprint.status)}
                          {sprint.status || 'planned'}
                        </span>
                      </div>
                      <div className="text-sm text-[var(--clr-text-muted)]">
                        {sprint.start_date} - {sprint.end_date}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => openSprintModal(sprint)}
                      className="p-1 text-[var(--clr-brand-primary)] hover:bg-[var(--clr-brand-primary)] bg-opacity-10 rounded"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => deleteSprint(sprint.id)}
                      className="p-1 text-[var(--clr-sev-blocked)] hover:bg-[var(--clr-sev-blocked)] bg-opacity-10 rounded"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {releases.length === 0 && getUnassignedSprints().length === 0 && (
          <div className="bg-[var(--clr-surface-card)] rounded-xl border p-12 text-center">
            <Rocket className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-[var(--clr-text-primary)] mb-2">No Releases Yet</h3>
            <p className="text-[var(--clr-text-secondary)] mb-4">Create your first release to organize your sprints</p>
            <button
              onClick={() => openReleaseModal()}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            >
              Create Release
            </button>
          </div>
        )}
      </div>

      {/* Release Modal */}
      {showReleaseModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowReleaseModal(false)} />
          <div className="relative bg-[var(--clr-surface-card)] rounded-xl shadow-2xl w-full max-w-md mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-[var(--clr-text-primary)]">
                {editingRelease ? 'Edit Release' : 'Create Release'}
              </h2>
              <button onClick={() => setShowReleaseModal(false)} className="p-1 hover:bg-[var(--clr-surface-hover)] rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={releaseForm.name}
                  onChange={(e) => setReleaseForm({...releaseForm, name: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="e.g., Q1 2026 Release"
                  data-testid="release-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={releaseForm.start_date}
                    onChange={(e) => setReleaseForm({...releaseForm, start_date: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                  <input
                    type="date"
                    value={releaseForm.end_date}
                    onChange={(e) => setReleaseForm({...releaseForm, end_date: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={releaseForm.description}
                  onChange={(e) => setReleaseForm({...releaseForm, description: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg h-20"
                  placeholder="Release goals and scope..."
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowReleaseModal(false)}
                className="px-4 py-2 border rounded-lg hover:bg-[var(--clr-surface-hover)]"
              >
                Cancel
              </button>
              <button
                onClick={saveRelease}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                data-testid="save-release-btn"
              >
                {editingRelease ? 'Update' : 'Create'} Release
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sprint Modal */}
      {showSprintModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowSprintModal(false)} />
          <div className="relative bg-[var(--clr-surface-card)] rounded-xl shadow-2xl w-full max-w-md mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-[var(--clr-text-primary)]">
                {editingSprint ? 'Edit Sprint' : 'Create Sprint'}
              </h2>
              <button onClick={() => setShowSprintModal(false)} className="p-1 hover:bg-[var(--clr-surface-hover)] rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={sprintForm.name}
                  onChange={(e) => setSprintForm({...sprintForm, name: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="e.g., Sprint 1"
                  data-testid="sprint-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Release</label>
                <select
                  value={selectedReleaseId || ''}
                  onChange={(e) => setSelectedReleaseId(e.target.value || null)}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="">No Release (Unassigned)</option>
                  {releases.map(r => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={sprintForm.start_date}
                    onChange={(e) => setSprintForm({...sprintForm, start_date: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                  <input
                    type="date"
                    value={sprintForm.end_date}
                    onChange={(e) => setSprintForm({...sprintForm, end_date: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={sprintForm.status}
                  onChange={(e) => setSprintForm({...sprintForm, status: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="planned">Planned</option>
                  <option value="active">Active</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Velocity</label>
                  <input
                    type="number"
                    value={sprintForm.velocity}
                    onChange={(e) => setSprintForm({...sprintForm, velocity: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Capacity</label>
                  <input
                    type="number"
                    value={sprintForm.capacity}
                    onChange={(e) => setSprintForm({...sprintForm, capacity: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Goal</label>
                <textarea
                  value={sprintForm.goal}
                  onChange={(e) => setSprintForm({...sprintForm, goal: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg h-16"
                  placeholder="Sprint goal..."
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowSprintModal(false)}
                className="px-4 py-2 border rounded-lg hover:bg-[var(--clr-surface-hover)]"
              >
                Cancel
              </button>
              <button
                onClick={saveSprint}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
                data-testid="save-sprint-btn"
              >
                {editingSprint ? 'Update' : 'Create'} Sprint
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReleasesPage;
